import { c as _c } from "react/compiler-runtime";
import { graphql, useQuery } from "@opensea/graphql";
import { Flex } from "@opensea/ui-kit";
import { omit } from "es-toolkit/object";
import { useEffect, useMemo, useState } from "react";
import { useTranslations } from "use-intl";
import { useSnapshot } from "valtio";
import { Button } from "../../../../../components/Button";
import { TransactionFullScreen } from "../../../../../components/TransactionFullScreen";
import { TransactionReceipt } from "../../../../../components/TransactionReceipt";
import { TransactionReceiptFragment } from "../../../../../components/TransactionReceipt/TransactionReceipt.fragment";
import { ViewItemsOnProfileButton } from "../../../../../components/ViewItemsOnProfileButton";
import { FlowState } from "../../../../../stores/flowStore";
import { useSetReceiptStatus, useSetReceiptStatusFragment } from "../../../../hooks/useSetReceiptStatus";
const FETCH_INTERVAL = 500;
const query = graphql(`
    query CollectionSweepFlowStatusQuery(
      $transactionIdentifiers: [TransactionIdentifier!]!
      $itemQuotes: [ItemQuoteInput!]!
    ) {
      sweepCollectionReceipt(
        transactionIdentifiers: $transactionIdentifiers
        itemQuotes: $itemQuotes
      ) {
        status
        itemReceipts {
          __typename
          item {
            id
          }
        }
        failedItemReceipts {
          __typename
          item {
            id
          }
        }
        ...useSetReceiptStatus
        ...TransactionReceipt
      }
    }
  `, [TransactionReceiptFragment, useSetReceiptStatusFragment]);
export const CollectionSweepStatus = t0 => {
  const $ = _c(71);
  const {
    transactionIdentifiers,
    collectionImageUrl,
    itemQuotes,
    itemImageMap
  } = t0;
  const t = useTranslations("wallet.CollectionSweepStatus");
  let t1;
  if ($[0] !== itemQuotes || $[1] !== transactionIdentifiers) {
    t1 = {
      query,
      variables: {
        transactionIdentifiers,
        itemQuotes
      },
      pause: true
    };
    $[0] = itemQuotes;
    $[1] = transactionIdentifiers;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [result, reexecuteQuery] = useQuery(t1);
  let t2;
  if ($[3] !== reexecuteQuery || $[4] !== result.data || $[5] !== result.fetching) {
    t2 = function pollForReceipt() {
      if (result.fetching || result.data?.sweepCollectionReceipt && result.data.sweepCollectionReceipt.status !== "PENDING") {
        return;
      }
      const timerId = setTimeout(() => {
        reexecuteQuery({
          requestPolicy: "network-only"
        });
      }, FETCH_INTERVAL);
      return () => clearTimeout(timerId);
    };
    $[3] = reexecuteQuery;
    $[4] = result.data;
    $[5] = result.fetching;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = result.data?.sweepCollectionReceipt;
  let t4;
  if ($[7] !== reexecuteQuery || $[8] !== result.fetching || $[9] !== t3) {
    t4 = [result.fetching, reexecuteQuery, t3];
    $[7] = reexecuteQuery;
    $[8] = result.fetching;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  useEffect(t2, t4);
  const receipt = result.data?.sweepCollectionReceipt;
  useSetReceiptStatus(receipt);
  const [receiptOpen, setReceiptOpen] = useState(false);
  if (!receipt || receipt.status === "PENDING") {
    let t5;
    if ($[11] !== t) {
      t5 = t("pending.description");
      $[11] = t;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    let t6;
    if ($[13] !== collectionImageUrl) {
      t6 = [collectionImageUrl];
      $[13] = collectionImageUrl;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    let t7;
    if ($[15] !== t) {
      t7 = t("pending.title");
      $[15] = t;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    let t8;
    if ($[17] !== t5 || $[18] !== t6 || $[19] !== t7) {
      t8 = <TransactionFullScreen body={t5} images={t6} loading={true} title={t7} />;
      $[17] = t5;
      $[18] = t6;
      $[19] = t7;
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    return t8;
  }
  if (receipt.status === "FAILED") {
    let t5;
    if ($[21] !== t) {
      t5 = t("failed.description");
      $[21] = t;
      $[22] = t5;
    } else {
      t5 = $[22];
    }
    let t6;
    if ($[23] !== receipt || $[24] !== receiptOpen || $[25] !== t) {
      t6 = receiptOpen ? <TransactionReceipt flowType="purchase" receipt={receipt} /> : <Button onClick={() => setReceiptOpen(true)} variant="secondary">{t("viewReceipt")}</Button>;
      $[23] = receipt;
      $[24] = receiptOpen;
      $[25] = t;
      $[26] = t6;
    } else {
      t6 = $[26];
    }
    let t7;
    if ($[27] !== collectionImageUrl) {
      t7 = [collectionImageUrl];
      $[27] = collectionImageUrl;
      $[28] = t7;
    } else {
      t7 = $[28];
    }
    let t8;
    if ($[29] !== t) {
      t8 = t("failed.title");
      $[29] = t;
      $[30] = t8;
    } else {
      t8 = $[30];
    }
    let t9;
    if ($[31] !== t5 || $[32] !== t6 || $[33] !== t7 || $[34] !== t8) {
      t9 = <TransactionFullScreen body={t5} bottom={t6} images={t7} monochrome={true} title={t8} />;
      $[31] = t5;
      $[32] = t6;
      $[33] = t7;
      $[34] = t8;
      $[35] = t9;
    } else {
      t9 = $[35];
    }
    return t9;
  }
  if (receipt.status === "PARTIAL_SUCCESS") {
    let t5;
    if ($[36] !== receipt.failedItemReceipts.length || $[37] !== receipt.itemReceipts.length || $[38] !== t) {
      t5 = t("partialSuccess.description", {
        itemsPurchasedCount: receipt.itemReceipts.length,
        itemsFailedCount: receipt.failedItemReceipts.length
      });
      $[36] = receipt.failedItemReceipts.length;
      $[37] = receipt.itemReceipts.length;
      $[38] = t;
      $[39] = t5;
    } else {
      t5 = $[39];
    }
    let t6;
    if ($[40] !== receipt || $[41] !== receiptOpen || $[42] !== t) {
      t6 = receiptOpen ? <TransactionReceipt flowType="purchase" receipt={receipt} /> : <Button onClick={() => setReceiptOpen(true)} variant="secondary">{t("viewReceipt")}</Button>;
      $[40] = receipt;
      $[41] = receiptOpen;
      $[42] = t;
      $[43] = t6;
    } else {
      t6 = $[43];
    }
    let t7;
    if ($[44] !== itemImageMap || $[45] !== receipt.itemReceipts) {
      let t8;
      if ($[47] !== itemImageMap) {
        t8 = item => item.item ? itemImageMap[item.item.id] : null;
        $[47] = itemImageMap;
        $[48] = t8;
      } else {
        t8 = $[48];
      }
      t7 = receipt.itemReceipts.map(t8);
      $[44] = itemImageMap;
      $[45] = receipt.itemReceipts;
      $[46] = t7;
    } else {
      t7 = $[46];
    }
    let t8;
    if ($[49] !== t) {
      t8 = t("partialSuccess.title");
      $[49] = t;
      $[50] = t8;
    } else {
      t8 = $[50];
    }
    let t9;
    if ($[51] !== t5 || $[52] !== t6 || $[53] !== t7 || $[54] !== t8) {
      t9 = <TransactionFullScreen body={t5} bottom={t6} images={t7} title={t8} />;
      $[51] = t5;
      $[52] = t6;
      $[53] = t7;
      $[54] = t8;
      $[55] = t9;
    } else {
      t9 = $[55];
    }
    return t9;
  }
  let t5;
  if ($[56] !== receipt || $[57] !== receiptOpen || $[58] !== t) {
    t5 = receiptOpen ? <TransactionReceipt flowType="purchase" receipt={receipt} /> : <Flex className="gap-4"><Button onClick={() => setReceiptOpen(true)} variant="secondary">{t("viewReceipt")}</Button><ViewItemsOnProfileButton itemIds={receipt.itemReceipts.flatMap(_temp)} /></Flex>;
    $[56] = receipt;
    $[57] = receiptOpen;
    $[58] = t;
    $[59] = t5;
  } else {
    t5 = $[59];
  }
  let t6;
  if ($[60] !== itemImageMap || $[61] !== receipt.itemReceipts) {
    let t7;
    if ($[63] !== itemImageMap) {
      t7 = item_0 => item_0.item ? itemImageMap[item_0.item.id] : null;
      $[63] = itemImageMap;
      $[64] = t7;
    } else {
      t7 = $[64];
    }
    t6 = receipt.itemReceipts.map(t7);
    $[60] = itemImageMap;
    $[61] = receipt.itemReceipts;
    $[62] = t6;
  } else {
    t6 = $[62];
  }
  let t7;
  if ($[65] !== t) {
    t7 = t("success.title");
    $[65] = t;
    $[66] = t7;
  } else {
    t7 = $[66];
  }
  let t8;
  if ($[67] !== t5 || $[68] !== t6 || $[69] !== t7) {
    t8 = <TransactionFullScreen bottom={t5} confetti={true} images={t6} title={t7} />;
    $[67] = t5;
    $[68] = t6;
    $[69] = t7;
    $[70] = t8;
  } else {
    t8 = $[70];
  }
  return t8;
};
export const CollectionSweepFlowStatus = t0 => {
  const $ = _c(9);
  const {
    collectionImageUrl
  } = t0;
  const {
    receiptTransactions,
    itemQuotes,
    itemImageMap
  } = useSnapshot(FlowState);
  let t1;
  let t2;
  if ($[0] !== itemQuotes) {
    t2 = itemQuotes.map(_temp2);
    $[0] = itemQuotes;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  t1 = t2;
  const itemQuoteInputs = t1;
  let t3;
  if ($[2] !== receiptTransactions) {
    t3 = receiptTransactions.map(_temp3);
    $[2] = receiptTransactions;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== collectionImageUrl || $[5] !== itemImageMap || $[6] !== itemQuoteInputs || $[7] !== t3) {
    t4 = <CollectionSweepStatus collectionImageUrl={collectionImageUrl} itemImageMap={itemImageMap} itemQuotes={itemQuoteInputs} transactionIdentifiers={t3} />;
    $[4] = collectionImageUrl;
    $[5] = itemImageMap;
    $[6] = itemQuoteInputs;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};
function _temp(itemReceipt) {
  return itemReceipt.item ? [itemReceipt.item.id] : [];
}
function _temp2(itemQuote) {
  return omit(itemQuote, ["orderId"]);
}
function _temp3(txn) {
  return {
    chain: txn.chain,
    transactionHash: txn.hash
  };
}